import { createRef, useEffect, useState } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import useStore from '../../../Hooks/useStore';
import Form from '../../../Form';
import SVG from '../../../SVG';
import { formatBRL } from '../../../../store/masks';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';

export function PageBannerFiles() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [getters, setters] = useStore();
  const {
    banner: { getBanner },
  } = getters;
  const {
    banner: {
      filterFormBanners,
      setBanner,
      editFormBanner,
      visualizeFormBanner,
      visualizeFormBanners,
      autocompleteBannerForms,
    },
    modal: { openSingleModal },
  } = setters;

  const ref = createRef<HTMLDivElement>();
  const searchFilter = getBanner('filters.form.filter');
  const bannerForms = getBanner('filters.form.data.data');

  const allChecks = !getBanner('formCheck').includes(false);
  const [printBatchLoading, setPrintBatchLoading] = useState(false);

  const [printLoadings, setPrintLoadings] = useState(
    bannerForms.map(() => false),
  );

  const [editLoadings, setEditLoadings] = useState(
    bannerForms.map(() => false),
  );

  const getPrintLoading = (index: number) => printLoadings[index];
  const setPrintLoading = (index: number, value: boolean) => {
    setPrintLoadings(printLoadings.map((v, i) => (index === i ? value : v)));
  };

  const getEditLoading = (index: number) => editLoadings[index];
  const setEditLoading = (index: number, value: boolean) => {
    setEditLoadings(editLoadings.map((v, i) => (index === i ? value : v)));
  };

  const setSearchFilter = (filter: SearchFilter) =>
    setBanner({
      field: 'filters.form.filter',
      value: filter,
    });
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    const value = e.target.value;
    let filter = setFilterColumn(field, value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = (
    field: keyof BannerState['autocompletes']['form'],
  ) =>
    getBanner(`autocompletes.form.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof BannerState['autocompletes']['form'],
  ) => {
    autocompleteBannerForms(field);
  };

  const setAutocompleteFilterField =
    (field: keyof BannerState['autocompletes']['form']) => (e) => {
      setBanner({
        field: `autocompletes.form.${field}.filter.value`,
        value: e.target.value,
      });
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  useEffect(() => {
    filterFormBanners();
  }, []);

  const headers: TableHeader[] = [
    {
      text: 'Produtos',
      value: 'products',
      width: '1fr',
      singularLabel: 'produto',
      pluralLabel: 'produtos',
      table: {
        headers: [
          {
            text: 'Código',
            value: 'code',
            width: '0.5fr',
          },
          {
            text: 'Código de barras',
            value: 'ean',
            width: '0.5fr',
          },
          {
            text: 'Dinâmica',
            value: 'dynamic',
            width: '0.5fr',
          },
          {
            text: 'Descrição',
            value: 'primary',
            width: '1fr',
          },
          {
            text: 'Preço',
            mask: ({ price }) => formatBRL(price),
            value: 'price',
            width: '1fr',
          },
          {
            text: 'Preço Promocional',
            mask: ({ promotional }) => formatBRL(promotional),
            value: 'promotional',
            width: '1fr',
          },
        ],
        items: [],
        size: 'small',
        bodyStyle: { height: 'auto' },
      },
    },
    {
      text: 'Criado por',
      value: 'signature',
      width: '1fr',
      filter: {
        value: getFilterField('signature'),
        onChange: setField('signature'),
        items: getAutocompleteItems('signature'),
        handleItems: () => setAutocompleteItems('signature'),
        label: 'Usuário',
      },
      sort: {
        value: getSortField('signature'),
        onChange: setSortField('signature'),
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        setSearchFilter(setFilterColumn('signature', '', searchFilter));
        setBanner({
          field: `autocompletes.form.signature.filter.value`,
          value: '',
        });
      },
    },
    {
      text: 'Criado em',
      value: 'createdAt',
      width: '1fr',
      mask: ({ createdAt }) => new Date(createdAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('createdAt'),
        onChange: setSortField('createdAt'),
      },
      interval: [
        {
          value: getFilterField('createdAt', 'GEQ'),
          onChange: setField('createdAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('createdAt', 'LT'),
          onChange: setField('createdAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.75,
      },
      onClear: () => {
        let filter = setFilterColumn('createdAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('createdAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '1fr',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      interval: [
        {
          value: getFilterField('updatedAt', 'GEQ'),
          onChange: setField('updatedAt', 'GEQ'),
          label: 'Data inicial',
        },
        {
          value: getFilterField('updatedAt', 'LT'),
          onChange: setField('updatedAt', 'LT'),
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 1,
      },
      onClear: () => {
        let filter = setFilterColumn('updatedAt', '', searchFilter, 'GEQ');
        filter = setFilterColumn('updatedAt', '', filter, 'LT');
        setSearchFilter(filter);
      },
    },
  ];

  headers.push({
    text: 'Quantidade',
    value: 'quantity',
    width: '120px',
  });

  const handlePrint = (e) => {
    setPrintBatchLoading(true);
    visualizeFormBanners()
      .catch(() => {
        enqueueSnackbar('Houve uma falha os cartazes', {
          variant: 'error',
        });
      })
      .finally(() => {
        setPrintBatchLoading(false);
      });
  };
  const handleDelete = () => {
    openSingleModal('confirmBannerFormsDeletion');
  };
  headers.push({
    text: (
      <>
        <Form
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {getBanner('formCheck').includes(true) && (
            <label
              style={{
                display: 'flex',
                columnGap: '10px',
              }}
            >
              <label
                style={{
                  cursor: 'pointer',
                }}
              >
                {true ? (
                  <SVG.Icon.Trash
                    onClick={handleDelete}
                    width={20}
                    height={20}
                  />
                ) : (
                  <SVG.Icon.Loader width={25} height={25} />
                )}
              </label>
              <label
                style={{
                  cursor: 'pointer',
                }}
              >
                {!printBatchLoading ? (
                  <SVG.Icon.Printer
                    onClick={handlePrint}
                    width={25}
                    height={25}
                  />
                ) : (
                  <SVG.Icon.Loader width={25} height={25} />
                )}
              </label>
            </label>
          )}
        </Form>
      </>
    ),
    value: 'control',
    width: '120px',
    style: {
      margin: 'auto 10px auto auto',
    },
  });
  headers.unshift({
    text: (
      <Form.Input.Checkbox
        checked={allChecks}
        onChange={(e) => {
          const isChecked = e.target.checked;
          setBanner({
            field: 'formCheck',
            value: getBanner('formCheck').map(() => isChecked),
          });
        }}
      />
    ),
    value: 'check',
    width: 'auto',
  });

  const handleFilter = () => {
    filterFormBanners();
  };

  let searchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(searchTimerId);
      searchTimerId = setTimeout(() => {
        handleFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(searchTimerId);
    };
  }, [searchFilter]);

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    setBanner({
      field: `formCheck.${index}`,
      value: !getBanner(`formCheck.${index}`),
    });
  };
  const total = getBanner('filters.form.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;

  const items = [...bannerForms].map((item, index) => {
    const isSelected = getBanner(`formCheck.${index}`) ?? false;
    return {
      ...item,
      $isSelected: isSelected,
      check: () => (
        <Form.Input.Checkbox
          checked={isSelected}
          onChange={onLineClickHandler(index)}
        />
      ),
      quantity: () => (
        <input
          type="number"
          style={{
            width: '100%',
          }}
          value={getBanner('formQuantity')[index]}
          onChange={(e) => {
            const value = +e.target.value;
            setBanner({
              field: `formQuantity.${index}`,
              value: Math.min(value > 1 ? value : 1, 100),
            });
          }}
        />
      ),
      control: () => (
        <label
          style={{
            display: 'flex',
            columnGap: '10px',
          }}
        >
          <label
            style={{
              cursor: 'pointer',
            }}
          >
            {!getEditLoading(index) ? (
              <SVG.Icon.Edit
                onClick={() => {
                  setEditLoading(index, true);
                  editFormBanner(item.id)
                    .then(() => {
                      navigate(`/tools/banner`);
                    })
                    .catch(() => {
                      enqueueSnackbar('Houve uma falha ao buscar o cartaz', {
                        variant: 'error',
                      });
                    })
                    .finally(() => {
                      setEditLoading(index, false);
                    });
                }}
                width={25}
                height={25}
              />
            ) : (
              <SVG.Icon.Loader width={25} height={25} />
            )}
          </label>
          <label
            style={{
              cursor: 'pointer',
            }}
          >
            {!getPrintLoading(index) ? (
              <SVG.Icon.Printer
                onClick={() => {
                  setPrintLoading(index, true);
                  setBanner({
                    field: 'formIndex',
                    value: index,
                  });
                  visualizeFormBanner(item.id).finally(() => {
                    setPrintLoading(index, false);
                  });
                }}
                width={25}
                height={25}
              />
            ) : (
              <SVG.Icon.Loader width={25} height={25} />
            )}
          </label>
        </label>
      ),
    };
  });

  return (
    <Page className="page-banner">
      <div
        ref={ref}
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 130px)',
          margin: '0 1rem',
          background: '#DDDDDD',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <Table
          headers={headers}
          items={items}
          onLineClick={onLineClickHandler}
          size="small"
          containerStyle={{ height: 'calc(100vh - 210px)' }}
        >
          <Table.Control
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalOfItems={total}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </Table>
      </div>
    </Page>
  );
}
